import type { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { formatDate } from '@side/pantry';
import { formatDisplayName } from '@/common/utils/formatDisplayName';
import {
  buildLeadSourceFilter,
  formatAmount,
  formatPercentage,
  getReportingPeriodDateRange,
} from '../helpers';
import {
  type PerformanceReportFiltersFormValues,
  PerformanceReportFilterTransactionStatus,
} from '../PerformanceReport/types';
import type { ReportsDataGroup } from '../ReportsConfiguration/useReportsRole';
import type {
  ReportingSummaryQuery,
  ReportingSummaryResult,
  TeamDisplayMap,
  UserDisplayMap,
} from '../types';

export type TransactionsTableResult = ReportingSummaryResult;

export type TransactionsTableRow = TransactionsTableResult & {
  id: string;
  agent: string;
  team: string;
};

export function mapTransactionToRow(
  r: TransactionsTableResult,
  displayMaps?: { usersById: UserDisplayMap; teamsById: TeamDisplayMap },
): TransactionsTableRow {
  const agentNames = (r.agentList ?? [])
    .map((id) => {
      const user = displayMaps?.usersById[id];
      return user
        ? formatDisplayName({
            firstName: user.firstName,
            lastName: user.lastName,
          })
        : id;
    })
    .join(', ');

  const team =
    displayMaps?.teamsById[r.teamIdentityId ?? '']?.alias ??
    r.teamIdentityId ??
    '';

  return {
    ...r,
    id: buildRowId(r),
    agent: agentNames,
    team,
  };
}

// Single source of truth for the API groupBy and the row id. Each unique
// server-side row is identified by these fields, so the client row id must
// include every one — commissionId alone is not unique (a single deal can
// produce multiple rows for listing + buying side, status splits, etc.) and
// reusing it collapses rows on MUI DataGrid.
// `\x00` delimiter is intentional: free-text fields like streetAddress and
// leadSourceName can contain `|`, which would risk accidental id collisions.
const TRANSACTIONS_GROUP_BY_FIELDS = [
  ['COMMISSION', 'commissionId'],
  ['TEAM', 'teamIdentityId'],
  ['CLOSE_DATE', 'closeDate'],
  ['STREET_ADDRESS', 'streetAddress'],
  ['CITY', 'city'],
  ['STATE', 'state'],
  ['ZIP_CODE', 'zipCode'],
  ['TRANSACTION_TYPE', 'transactionType'],
  ['TRANSACTION_SIDE', 'side'],
  ['TRANSACTION_STATUS', 'transactionStatus'],
  ['LEAD_SOURCE', 'leadSourceName'],
  ['LEAD_SOURCE_DETAIL', 'leadSourceDetail'],
] as const;

function buildRowId(r: TransactionsTableResult): string {
  return TRANSACTIONS_GROUP_BY_FIELDS.map(
    ([, rowField]) => r[rowField as keyof TransactionsTableResult],
  ).join('\x00');
}

const TRANSACTION_TYPE_LABEL: Record<
  NonNullable<TransactionsTableResult['transactionType']>,
  string
> = {
  LISTING: 'Listing',
  OFFER: 'Offer',
  REFERRAL: 'Referral',
  LEASE_LANDLORD: 'Lease (Landlord)',
  LEASE_TENANT: 'Lease (Tenant)',
};

const TRANSACTION_STATUS_LABEL: Partial<
  Record<NonNullable<TransactionsTableResult['transactionStatus']>, string>
> = {
  CLOSED: 'Closed',
  PENDING_OR_IN_ESCROW: 'Pending',
};

export enum TransactionsTableColumns {
  closeDate = 'closeDate',
  transactionType = 'transactionType',
  streetAddress = 'streetAddress',
  transactionStatus = 'transactionStatus',
  salesVolume = 'salesVolume',
  agent = 'agent',
  team = 'team',
  averageCommissionPercentage = 'averageCommissionPercentage',
  startingGrossCommission = 'startingGrossCommission',
  city = 'city',
  zipCode = 'zipCode',
  listPrice = 'listPrice',
  preFees = 'preFees',
  adjustedGrossCommission = 'adjustedGrossCommission',
  leadSourceName = 'leadSourceName',
  leadSourceDetail = 'leadSourceDetail',
}

export const columns: GridColDef<TransactionsTableRow>[] = [
  {
    field: TransactionsTableColumns.closeDate,
    headerName: 'Close',
    valueFormatter: (value) => formatDate(value ?? '', 'shortDate'),
    flex: 0.5,
    minWidth: 100,
    hideable: false,
    sortable: true,
  },
  {
    field: TransactionsTableColumns.transactionType,
    headerName: 'Type',
    valueFormatter: (value: TransactionsTableRow['transactionType']) =>
      value != null ? (TRANSACTION_TYPE_LABEL[value] ?? value) : '',
    minWidth: 100,
    flex: 0.9,
    hideable: false,
    sortable: true,
  },
  {
    field: TransactionsTableColumns.streetAddress,
    headerName: 'Address',
    minWidth: 220,
    flex: 1.6,
    hideable: false,
    sortable: true,
    valueFormatter: (v) => v ?? '',
  },
  {
    field: TransactionsTableColumns.transactionStatus,
    headerName: 'Status',
    valueFormatter: (value: TransactionsTableRow['transactionStatus']) =>
      value != null ? (TRANSACTION_STATUS_LABEL[value] ?? value) : '',
    minWidth: 110,
    flex: 0.5,
    hideable: true,
    sortable: true,
  },
  {
    field: TransactionsTableColumns.salesVolume,
    headerName: 'Deal Value',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    hideable: false,
    sortable: true,
  },
  {
    field: TransactionsTableColumns.agent,
    headerName: 'Agent',
    // Sorting disabled: the BE only projects the `agent` column in the
    // summary CTE when `groupBy` includes AGENT, which this table does not
    // request. Re-enable once payment-service always projects the column
    // (or maps sortBy=agent to an existing column).
    sortable: false,
    minWidth: 178,
    flex: 1,
    hideable: false,
    valueFormatter: (v) => v ?? '',
  },
  {
    field: TransactionsTableColumns.team,
    headerName: 'Team',
    minWidth: 138,
    flex: 1,
    hideable: false,
    sortable: true,
  },
  {
    field: TransactionsTableColumns.averageCommissionPercentage,
    headerName: 'Commission %',
    valueFormatter: formatPercentage,
    minWidth: 100,
    flex: 0.5,
    sortable: true,
  },
  {
    field: TransactionsTableColumns.startingGrossCommission,
    headerName: 'GCI',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: TransactionsTableColumns.city,
    headerName: 'City',
    minWidth: 120,
    flex: 1,
    valueFormatter: (v) => v ?? '',
    sortable: true,
  },
  {
    field: TransactionsTableColumns.zipCode,
    headerName: 'Zip',
    minWidth: 100,
    flex: 0.5,
    valueFormatter: (v) => v ?? '',
    sortable: true,
  },
  {
    field: TransactionsTableColumns.listPrice,
    headerName: 'List Price',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: TransactionsTableColumns.preFees,
    headerName: 'Pre-Split Deductions',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: TransactionsTableColumns.adjustedGrossCommission,
    headerName: 'Post-Referral GCI',
    valueFormatter: formatAmount,
    minWidth: 120,
    flex: 1,
    sortable: true,
  },
  {
    field: TransactionsTableColumns.leadSourceName,
    headerName: 'Lead Source',
    minWidth: 120,
    flex: 1,
    valueFormatter: (v) => v ?? '',
    sortable: true,
  },
  {
    field: TransactionsTableColumns.leadSourceDetail,
    headerName: 'Lead Source Detail',
    minWidth: 120,
    flex: 1,
    valueFormatter: (v) => v ?? '',
    sortable: false,
  },
];

export const VISIBILITY_MODEL: Record<TransactionsTableColumns, boolean> = {
  closeDate: true,
  transactionType: true,
  streetAddress: true,
  transactionStatus: true,
  salesVolume: true,
  agent: true,
  team: true,
  averageCommissionPercentage: true,
  startingGrossCommission: true,
  city: false,
  zipCode: false,
  listPrice: false,
  preFees: false,
  adjustedGrossCommission: false,
  leadSourceName: false,
  leadSourceDetail: false,
};

export const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

export const DEFAULT_PAGE_SIZE = 25;

export const transactionsDataGroups: Record<
  TransactionsTableColumns,
  ReportsDataGroup
> = {
  closeDate: 'agent',
  transactionType: 'agent',
  streetAddress: 'agent',
  transactionStatus: 'agent',
  salesVolume: 'agent',
  agent: 'agent',
  team: 'agent',
  averageCommissionPercentage: 'agent',
  startingGrossCommission: 'agent',
  city: 'agent',
  zipCode: 'agent',
  listPrice: 'agent',
  preFees: 'agent',
  adjustedGrossCommission: 'agent',
  leadSourceName: 'agent',
  leadSourceDetail: 'agent',
};

const TRANSACTIONS_GROUP_BY = TRANSACTIONS_GROUP_BY_FIELDS.map(
  ([apiKey]) => apiKey,
) as NonNullable<ReportingSummaryQuery['groupBy']>;

const ALL_TRANSACTION_TYPES: NonNullable<
  ReportingSummaryQuery['transactionType']
> = ['LISTING', 'OFFER', 'REFERRAL', 'LEASE_LANDLORD', 'LEASE_TENANT'];

export function buildTransactionsSummaryQuery(
  filters: PerformanceReportFiltersFormValues | undefined,
  sortModel: GridSortModel,
  viewReportAs: string,
  { offset, limit }: { offset: number; limit: number },
  allTimeStartDate?: string,
): ReportingSummaryQuery {
  const { closingDateStart, closingDateEnd } = getReportingPeriodDateRange(
    filters?.reportingPeriod ?? new Date().getFullYear(),
    { start: filters?.customDateStart, end: filters?.customDateEnd },
    allTimeStartDate,
  );
  const sort = sortModel[0];
  const sortBy = sort?.field ?? 'closeDate';
  const sortDirection: 'ASC' | 'DESC' = sort?.sort === 'asc' ? 'ASC' : 'DESC';
  const leadSourceFilter = buildLeadSourceFilter(filters);

  return {
    viewReportAs,
    offset,
    limit,
    sortBy: sortBy as ReportingSummaryQuery['sortBy'],
    sortDirection,
    closingDateStart,
    closingDateEnd,
    groupBy: TRANSACTIONS_GROUP_BY,
    transactionStatus: filters?.status.length
      ? filters.status.map((status) =>
          status === PerformanceReportFilterTransactionStatus.closed
            ? 'CLOSED'
            : 'PENDING_OR_IN_ESCROW',
        )
      : ['CLOSED'],
    transactionType: filters?.transactionType.length
      ? filters.transactionType
      : ALL_TRANSACTION_TYPES,
    excludeTerminatedAgents: filters?.hideTerminatedAgents ?? false,
    agentIdentityIds: filters?.agents?.length
      ? filters.agents.map((a) => a.value)
      : undefined,
    teamIdentityIds: filters?.teams?.length
      ? filters.teams.map((t) => t.value)
      : undefined,
    ...leadSourceFilter,
  };
}
